// ZEC → BTC / ZEC → USD converter pages — paste placeholders into Strapi `about_this_chart` HTML.

import { escapeHtml, SEO_VARIABLE_VALUE_MARKUP_CLASS } from "@/utils/seoVariables";

const PLACEHOLDER_RE = /\{\{\s*([a-z0-9_.]+)\s*\}\}/g;

const EM_DASH = "\u2014";

function replaceKnownConverterPlaceholders(
  html: string,
  values: Partial<Record<string, string>>,
  knownIds: Set<string>
): string {
  return html.replace(PLACEHOLDER_RE, (full, rawId: string) => {
    if (!knownIds.has(rawId)) return full;
    const v = values[rawId];
    if (v === undefined) return full;
    const inner = escapeHtml(v);
    return `<span class="${SEO_VARIABLE_VALUE_MARKUP_CLASS}">${inner}</span>`;
  });
}

// ---------------------------------------------------------------------------
// ZEC → BTC
// ---------------------------------------------------------------------------

export const ZEC_TO_BTC_SEO_VARIABLE_IDS = [
  "0.1_zec_to_btc",
  "0.5_zec_to_btc",
  "1_zec_to_btc",
  "5_zec_to_btc",
  "10_zec_to_btc",
  "50_zec_to_btc",
  "100_zec_to_btc",
] as const;

export type ZecToBtcSeoVariableId = (typeof ZEC_TO_BTC_SEO_VARIABLE_IDS)[number];

export type ZecToBtcSeoVariableValues = Partial<Record<ZecToBtcSeoVariableId, string>>;

export const ZEC_TO_BTC_SEO_AMOUNT_ROWS: readonly {
  zecAmount: number;
  id: ZecToBtcSeoVariableId;
}[] = [
  { zecAmount: 0.1, id: "0.1_zec_to_btc" },
  { zecAmount: 0.5, id: "0.5_zec_to_btc" },
  { zecAmount: 1, id: "1_zec_to_btc" },
  { zecAmount: 5, id: "5_zec_to_btc" },
  { zecAmount: 10, id: "10_zec_to_btc" },
  { zecAmount: 50, id: "50_zec_to_btc" },
  { zecAmount: 100, id: "100_zec_to_btc" },
];

const ZEC_TO_BTC_KNOWN_IDS = new Set<string>(ZEC_TO_BTC_SEO_VARIABLE_IDS);

export interface ZecToBtcSeoVariableDefinition {
  id: ZecToBtcSeoVariableId;
  placeholder: string;
  description: string;
}

export const ZEC_TO_BTC_SEO_VARIABLE_DEFINITIONS: readonly ZecToBtcSeoVariableDefinition[] =
  ZEC_TO_BTC_SEO_AMOUNT_ROWS.map(({ zecAmount, id }) => ({
    id,
    placeholder: `{{ ${id} }}`,
    description: `BTC equivalent of ${zecAmount} ZEC at the current rate (8 decimal places).`,
  }));

/** Replace known ZEC→BTC placeholders; other `{{ ... }}` tokens are left unchanged. */
export function replaceZecToBtcSeoVariables(
  html: string,
  values: ZecToBtcSeoVariableValues
): string {
  return replaceKnownConverterPlaceholders(html, values, ZEC_TO_BTC_KNOWN_IDS);
}

/** Values for substitution: BTC amounts or em dash when rate is unavailable. */
export function buildZecToBtcSeoValues(
  exchangeRate: number | null
): Record<ZecToBtcSeoVariableId, string> {
  const out = {} as Record<ZecToBtcSeoVariableId, string>;
  const rate = exchangeRate;
  const ok = rate !== null && rate > 0;
  for (const { zecAmount, id } of ZEC_TO_BTC_SEO_AMOUNT_ROWS) {
    out[id] = ok ? (zecAmount * rate).toFixed(8) : EM_DASH;
  }
  return out;
}

// ---------------------------------------------------------------------------
// ZEC → USD
// ---------------------------------------------------------------------------

export const ZEC_TO_USD_SEO_VARIABLE_IDS = [
  "0.1_zec_to_usd",
  "0.5_zec_to_usd",
  "1_zec_to_usd",
  "5_zec_to_usd",
  "10_zec_to_usd",
  "50_zec_to_usd",
  "100_zec_to_usd",
] as const;

export type ZecToUsdSeoVariableId = (typeof ZEC_TO_USD_SEO_VARIABLE_IDS)[number];

export type ZecToUsdSeoVariableValues = Partial<Record<ZecToUsdSeoVariableId, string>>;

export const ZEC_TO_USD_SEO_AMOUNT_ROWS: readonly {
  zecAmount: number;
  id: ZecToUsdSeoVariableId;
}[] = [
  { zecAmount: 0.1, id: "0.1_zec_to_usd" },
  { zecAmount: 0.5, id: "0.5_zec_to_usd" },
  { zecAmount: 1, id: "1_zec_to_usd" },
  { zecAmount: 5, id: "5_zec_to_usd" },
  { zecAmount: 10, id: "10_zec_to_usd" },
  { zecAmount: 50, id: "50_zec_to_usd" },
  { zecAmount: 100, id: "100_zec_to_usd" },
];

const ZEC_TO_USD_KNOWN_IDS = new Set<string>(ZEC_TO_USD_SEO_VARIABLE_IDS);

export interface ZecToUsdSeoVariableDefinition {
  id: ZecToUsdSeoVariableId;
  placeholder: string;
  description: string;
}

export const ZEC_TO_USD_SEO_VARIABLE_DEFINITIONS: readonly ZecToUsdSeoVariableDefinition[] =
  ZEC_TO_USD_SEO_AMOUNT_ROWS.map(({ zecAmount, id }) => ({
    id,
    placeholder: `{{ ${id} }}`,
    description: `USD equivalent of ${zecAmount} ZEC at the current rate (2 decimal places, same as the calculator).`,
  }));

/** Replace known ZEC→USD placeholders; other `{{ ... }}` tokens are left unchanged. */
export function replaceZecToUsdSeoVariables(
  html: string,
  values: ZecToUsdSeoVariableValues
): string {
  return replaceKnownConverterPlaceholders(html, values, ZEC_TO_USD_KNOWN_IDS);
}

/** Values for substitution: USD amounts or em dash when rate is unavailable. */
export function buildZecToUsdSeoValues(
  exchangeRate: number | null
): Record<ZecToUsdSeoVariableId, string> {
  const out = {} as Record<ZecToUsdSeoVariableId, string>;
  const rate = exchangeRate;
  const ok = rate !== null && rate > 0;
  for (const { zecAmount, id } of ZEC_TO_USD_SEO_AMOUNT_ROWS) {
    out[id] = ok ? (zecAmount * rate).toFixed(2) : EM_DASH;
  }
  return out;
}
